/**
 * Combobox — searchable picker (grouped or flat) built on cmdk + Radix Popover.
 *
 * Trigger styled like `SelectTrigger` (same h-9, paper bg, line border, chevron).
 * Content: search input + scrollable grouped/flat list. Keyboard nav handled by
 * cmdk (arrows, Enter, Escape, type-ahead). Selected item shows Check icon.
 */
import * as React from "react";
import { Command } from "cmdk";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover.js";
import { cn } from "../lib/cn.js";

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxGroup {
  label: string;
  options: ComboboxOption[];
}

export interface ComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
  /** Grouped options — takes precedence over `options`. */
  groups?: ComboboxGroup[] | undefined;
  /** Flat (ungrouped) options. */
  options?: ComboboxOption[] | undefined;
  placeholder?: string | undefined;
  searchPlaceholder?: string | undefined;
  emptyMessage?: string | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
}

export function Combobox({
  value,
  onValueChange,
  groups,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyMessage = "No results.",
  disabled,
  className,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch("");
  };

  const selectedLabel = React.useMemo(() => {
    if (!value) return undefined;
    if (groups) {
      for (const g of groups) {
        const found = g.options.find((o) => o.value === value);
        if (found) return found.label;
      }
    }
    if (options) {
      const found = options.find((o) => o.value === value);
      if (found) return found.label;
    }
    return undefined;
  }, [value, groups, options]);

  const handleSelect = (nextValue: string) => {
    onValueChange(nextValue);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex h-9 w-full items-center justify-between gap-2 rounded-sm",
            "bg-paper border border-line px-3 text-sm text-ink",
            "transition-colors hover:border-line-2",
            "focus-visible:outline-hidden focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent-subtle",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
        >
          <span className={cn("truncate", !selectedLabel && "text-muted")}>
            {selectedLabel ?? placeholder}
          </span>
          <ChevronDown aria-hidden size={14} strokeWidth={2} className="shrink-0 text-muted" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command
          shouldFilter
          className="flex flex-col"
          value={value}
        >
          <div className="flex items-center gap-2 border-b border-line px-3">
            <Search aria-hidden size={14} className="shrink-0 text-muted" />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder={searchPlaceholder}
              className="h-9 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-muted"
            />
          </div>
          <Command.List className="max-h-[min(20rem,var(--radix-popover-content-available-height,20rem)-44px)] overflow-y-auto p-1">
            <Command.Empty className="px-3 py-6 text-center text-sm text-muted">
              {emptyMessage}
            </Command.Empty>
            {groups
              ? groups.map((group) => (
                  <Command.Group
                    key={group.label}
                    heading={group.label}
                    className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.14em] [&_[cmdk-group-heading]]:text-muted"
                  >
                    {group.options.map((opt) => (
                      <ComboboxItem
                        key={opt.value}
                        option={opt}
                        selected={opt.value === value}
                        onSelect={handleSelect}
                      />
                    ))}
                  </Command.Group>
                ))
              : (options ?? []).map((opt) => (
                  <ComboboxItem
                    key={opt.value}
                    option={opt}
                    selected={opt.value === value}
                    onSelect={handleSelect}
                  />
                ))}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ComboboxItem({
  option,
  selected,
  onSelect,
}: {
  option: ComboboxOption;
  selected: boolean;
  onSelect: (value: string) => void;
}) {
  return (
    <Command.Item
      value={option.value}
      keywords={[option.label]}
      onSelect={() => onSelect(option.value)}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm",
        "py-1.5 pl-2 pr-8 text-sm text-ink outline-hidden",
        "data-[selected=true]:bg-accent-subtle data-[selected=true]:text-ink",
        "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
      )}
    >
      <span className="truncate">{option.label}</span>
      {selected ? (
        <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
          <Check aria-hidden size={12} strokeWidth={2.2} className="text-accent" />
        </span>
      ) : null}
    </Command.Item>
  );
}
